import {
  CoiSpeed,
  InsuranceType,
  type PricedQuoteCard,
  PricingKind,
  QuoteCardStage,
} from '@/components/quotes/quoteCardTypes'
import { CoverageType } from '@/models/insuranceCoverage'

/** Public marketing CDN — the same icons the carrier logo grid pulls. */
const CARRIER_LOGO_BASE = 'https://static.cai.insure/carriers'

// Relative, so the card's "days until this quote expires" line never drifts into the past.
const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString()

const FINANCING = {
  isStandard: true,
  description: 'Split your premium into 10 monthly payments at 0% APR.',
}

interface QuoteSeed {
  id: string
  carrierName: string
  icon: string
  rating: PricedQuoteCard['carrierRating']
  coiSpeed: CoiSpeed
  insuranceType: InsuranceType
  autoLiability: number
  cargo: number
  /** Only the two cards the animation opens need the detail an expanded card shows. */
  expanded?: boolean
}

const SEEDS: QuoteSeed[] = [
  {
    id: 'progressive',
    carrierName: 'Progressive',
    icon: 'progressive-icon.png',
    rating: 'A+',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    insuranceType: InsuranceType.ADMITTED,
    autoLiability: 12_600,
    cargo: 2_880,
  },
  {
    id: 'canal',
    carrierName: 'Canal',
    icon: 'canal-icon.png',
    rating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    insuranceType: InsuranceType.ADMITTED,
    autoLiability: 11_600,
    cargo: 2_640,
  },
  {
    id: 'nirvana',
    carrierName: 'Nirvana',
    icon: 'nirvana-icon.png',
    rating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    insuranceType: InsuranceType.NON_ADMITTED,
    autoLiability: 10_400,
    cargo: 2_460,
    expanded: true,
  },
  {
    id: 'geico',
    carrierName: 'GEICO',
    icon: 'geico-icon.png',
    rating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    insuranceType: InsuranceType.ADMITTED,
    autoLiability: 11_000,
    cargo: 2_540,
    expanded: true,
  },
  {
    id: 'corgi',
    carrierName: 'Corgi',
    icon: 'corgi-icon.png',
    rating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    insuranceType: InsuranceType.ADMITTED,
    autoLiability: 12_200,
    cargo: 2_780,
  },
  {
    id: 'biberk',
    carrierName: 'Biberk',
    icon: 'biberk-icon.png',
    rating: 'A-',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    insuranceType: InsuranceType.ADMITTED,
    autoLiability: 13_100,
    cargo: 3_020,
  },
]

const CLAIMS = {
  geico: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: true,
    waysToFile: 'File online, through the GEICO mobile app, or by phone',
    supportAvailability: 'Available anytime',
    payoutSpeed: 'As little as 48 hours',
    waysToGetPaid: 'Digital payment or mail',
  },
  nirvana: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: false,
    waysToFile: 'File online through the Nirvana platform, by email, or by phone',
    supportAvailability: '24/7 reporting',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
} as const

function toQuote(seed: QuoteSeed): PricedQuoteCard {
  return {
    id: seed.id,
    carrierName: seed.carrierName,
    iconUrl: `${CARRIER_LOGO_BASE}/${seed.icon}`,
    iconAlt: seed.carrierName,
    carrierRating: seed.rating,
    coiSpeed: seed.coiSpeed,
    insuranceType: seed.insuranceType,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(4),
    coverages: [
      { type: CoverageType.AUTO_LIABILITY, premium: seed.autoLiability, limit: 1_000_000, deductible: 0 },
      { type: CoverageType.MOTOR_TRUCK_CARGO, premium: seed.cargo, limit: 100_000, deductible: 1_000 },
    ],
    ...(seed.expanded
      ? {
          financing: FINANCING,
          claimsCapabilities: seed.id === 'geico' ? CLAIMS.geico : CLAIMS.nirvana,
        }
      : {}),
  }
}

/** The row, left to right. The two centre cards are the ones the animation opens. */
export const ANIMATION_QUOTES: PricedQuoteCard[] = SEEDS.map(toQuote)

/** Opens first, and the one the cart click lands on. */
export const ANCHOR_INDEX = 2
/** Opens second, beside the anchor. */
export const COMPARED_INDEX = 3
